import { Modal } from '@/components/admin/Modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { type FormEvent, useEffect, useState } from 'react';

/** ResponsePromptSlugSchema 와 동일 — 클라이언트 즉시 검증용. */
const SLUG_REGEX = /^[a-z0-9][a-z0-9.-]*[a-z0-9]$/;
const SLUG_HINT = '영소문자/숫자/점/하이픈만 가능. 예: query.language-detect';

interface CreateProps {
  open: boolean;
  onClose: () => void;
  type: 'folder' | 'file';
  parentName: string | null;
  isPending: boolean;
  onSubmit: (input: { name: string; description: string; slug?: string }) => void;
}

export function PromptCreateModal({
  open,
  onClose,
  type,
  parentName,
  isPending,
  onSubmit,
}: CreateProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setSlug('');
    }
  }, [open]);
  const title = type === 'folder' ? '폴더 추가' : '프롬프트 추가';
  const placeholder = type === 'folder' ? '예: 질의 분석' : '예: 언어 감지';

  const slugInvalid = type === 'file' && slug.trim() !== '' && !SLUG_REGEX.test(slug.trim());
  const slugMissing = type === 'file' && slug.trim() === '';

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isPending) return;
    const t = name.trim();
    if (!t) return;
    if (type === 'file' && (slugMissing || slugInvalid)) return;
    onSubmit({
      name: t,
      description: description.trim(),
      slug: type === 'file' ? slug.trim() : undefined,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={parentName ? `상위: ${parentName}` : '루트에 추가'}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isPending}>
            취소
          </Button>
          <Button
            type="submit"
            form="rp-create-form"
            disabled={isPending || !name.trim() || slugInvalid || slugMissing}
          >
            {isPending ? '추가 중…' : '추가'}
          </Button>
        </div>
      }
    >
      <form id="rp-create-form" onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-xs text-foreground-muted mb-1" htmlFor="rp-create-name">
            이름
          </label>
          <Input
            id="rp-create-name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={placeholder}
          />
        </div>
        {type === 'file' ? (
          <>
            <div>
              <label className="block text-xs text-foreground-muted mb-1" htmlFor="rp-create-slug">
                slug <span className="text-destructive">*</span>
              </label>
              <Input
                id="rp-create-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="query.language-detect"
                aria-invalid={slugInvalid}
              />
              <div
                className={`mt-0.5 text-[10px] ${
                  slugInvalid ? 'text-destructive' : 'text-foreground-muted'
                }`}
              >
                {slugInvalid
                  ? '형식이 맞지 않습니다.'
                  : `파이프라인 코드에서 부르는 식별자. ${SLUG_HINT}`}
              </div>
            </div>
            <div>
              <label className="block text-xs text-foreground-muted mb-1" htmlFor="rp-create-desc">
                설명 (선택)
              </label>
              <Textarea
                id="rp-create-desc"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="용도/맥락을 짧게 적어두면 운영에 도움이 됩니다."
              />
            </div>
          </>
        ) : null}
      </form>
    </Modal>
  );
}

interface RenameProps {
  open: boolean;
  onClose: () => void;
  current: {
    name: string;
    description: string | null;
    type: 'folder' | 'file';
    slug: string | null;
  } | null;
  isPending: boolean;
  onSubmit: (input: { name: string; description: string; slug?: string }) => void;
}

export function PromptRenameModal({ open, onClose, current, isPending, onSubmit }: RenameProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  useEffect(() => {
    if (open && current) {
      setName(current.name);
      setDescription(current.description ?? '');
      setSlug(current.slug ?? '');
    }
  }, [open, current]);
  if (!current) return null;

  const slugInvalid =
    current.type === 'file' && slug.trim() !== '' && !SLUG_REGEX.test(slug.trim());
  const slugMissing = current.type === 'file' && slug.trim() === '';

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isPending) return;
    const t = name.trim();
    if (!t) return;
    if (current.type === 'file' && (slugInvalid || slugMissing)) return;
    onSubmit({
      name: t,
      description: description.trim(),
      slug: current.type === 'file' ? slug.trim() : undefined,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="이름 변경"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isPending}>
            취소
          </Button>
          <Button
            type="submit"
            form="rp-rename-form"
            disabled={isPending || !name.trim() || slugInvalid || slugMissing}
          >
            {isPending ? '저장 중…' : '저장'}
          </Button>
        </div>
      }
    >
      <form id="rp-rename-form" onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-xs text-foreground-muted mb-1" htmlFor="rp-rename-name">
            이름
          </label>
          <Input
            id="rp-rename-name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        {current.type === 'file' ? (
          <>
            <div>
              <label className="block text-xs text-foreground-muted mb-1" htmlFor="rp-rename-slug">
                slug <span className="text-destructive">*</span>
              </label>
              <Input
                id="rp-rename-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="query.language-detect"
                aria-invalid={slugInvalid}
              />
              <div
                className={`mt-0.5 text-[10px] ${
                  slugInvalid ? 'text-destructive' : 'text-foreground-muted'
                }`}
              >
                {slugInvalid
                  ? '형식이 맞지 않습니다.'
                  : `pipeline 식별자 — 가급적 변경하지 마세요. ${SLUG_HINT}`}
              </div>
            </div>
            <div>
              <label className="block text-xs text-foreground-muted mb-1" htmlFor="rp-rename-desc">
                설명
              </label>
              <Textarea
                id="rp-rename-desc"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </>
        ) : null}
      </form>
    </Modal>
  );
}
